import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/cn'

const syncProgressVariants = cva('flex flex-col gap-1.5', {
  variants: {
    size: {
      sm: 'w-24',
      md: 'w-36',
      lg: 'w-48',
      full: 'w-full',
    },
    textSize: {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-xl',
    },
  },
  defaultVariants: {
    size: 'sm',
    textSize: 'lg',
  },
})

const numberVariants = cva('font-bold tabular-nums leading-none text-center', {
  variants: {
    variant: {
      default: 'text-primary',
      success: 'text-success',
      destructive: 'text-destructive',
    },
  },
  defaultVariants: { variant: 'default' },
})

const trackVariants = cva('h-2 w-full overflow-hidden rounded-full', {
  variants: {
    variant: {
      default: 'bg-primary/30',
      success: 'bg-success/30',
      destructive: 'bg-destructive/30',
    },
  },
  defaultVariants: { variant: 'default' },
})

const barVariants = cva('h-full rounded-full transition-all duration-300 ease-in-out', {
  variants: {
    variant: {
      default: 'bg-primary',
      success: 'bg-success',
      destructive: 'bg-destructive',
    },
  },
  defaultVariants: { variant: 'default' },
})

export interface SyncProgressProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof syncProgressVariants>,
    VariantProps<typeof barVariants> {
  value: number
  showPercent?: boolean
}

const SyncProgress = React.forwardRef<HTMLDivElement, SyncProgressProps>(
  ({ value, variant, showPercent = true, size, textSize, className, ...props }, ref) => {
    const clamped = Math.min(100, Math.max(0, value))

    return (
      <div ref={ref} className={cn(syncProgressVariants({ size, textSize }), className)} {...props}>
        <span className={cn(numberVariants({ variant }))}>
          {clamped}
          {showPercent && '%'}
        </span>

        <div className={cn(trackVariants({ variant }))}>
          <div
            className={cn(barVariants({ variant }))}
            style={{ width: `${clamped}%` }}
            role='progressbar'
            aria-label='Progress'
            aria-valuenow={clamped}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>
    )
  },
)
SyncProgress.displayName = 'SyncProgress'

export { SyncProgress, syncProgressVariants }
